import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import PdfSideViewer from '../../PdfSideViewer';
import { useBioEditor } from '../hooks/useBioEditor';

export default function BioEditorDialog({ ean, open, initialBios, onSave, onClose }) {
  const {
    holders, holdersLoading, holdersError, loadHolders,
    bios, setBioText, setAllBios,
    estimatedLength, needsEditingThreshold, likelyExceedsTwoPages,
    previewUrl, previewLoading, previewError, updatePreview,
    reset,
  } = useBioEditor();

  useEffect(() => {
    if (!open || !ean) return;
    let cancelled = false;
    (async () => {
      const loaded = await loadHolders(ean);
      if (cancelled) return;
      const defaults = Object.fromEntries(loaded.map((h) => [h.copyrightHolderId, h.bio || '']));
      const effectiveBios = initialBios ? { ...defaults, ...initialBios } : defaults;
      setAllBios(effectiveBios);
      await updatePreview(ean, effectiveBios);
    })();
    return () => { cancelled = true; };
    // loadHolders/updatePreview/setAllBios are recreated every render — only re-run when the dialog target changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ean]);

  const handleClose = () => {
    if (holders.length > 0) onSave(ean, bios);
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Edit Bios — {ean}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', gap: 3, overflow: 'hidden', flex: 1, pb: 3 }}>
        <Paper elevation={2} sx={{ width: '45%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">Biographies</Typography>
            <Typography variant="caption" color="text.secondary">
              Changes affect the PDF download but aren't saved permanently.
            </Typography>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {holdersLoading && <CircularProgress size={24} />}
            {holdersError && <Alert severity="error">{holdersError}</Alert>}
            {!holdersLoading && !holdersError && holders.length === 0 && (
              <Alert severity="info">No authors/editors with a biography found.</Alert>
            )}
            <Stack spacing={2}>
              {holders.map((h) => (
                <TextField
                  key={h.copyrightHolderId}
                  label={`${h.displayName} (${h.holderType})`}
                  value={bios[h.copyrightHolderId] ?? ''}
                  onChange={(e) => setBioText(h.copyrightHolderId, e.target.value)}
                  multiline
                  minRows={4}
                  fullWidth
                />
              ))}
            </Stack>
          </Box>

          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            {likelyExceedsTwoPages && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Text may be too long for 2 pages ({estimatedLength} / {needsEditingThreshold} characters).
              </Alert>
            )}
            {previewError && <Alert severity="error" sx={{ mb: 2 }}>{previewError}</Alert>}
            <Button
              variant="contained"
              onClick={() => updatePreview(ean)}
              disabled={previewLoading || holdersLoading}
              startIcon={previewLoading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
              fullWidth
            >
              {previewLoading ? 'Updating…' : 'Update Preview'}
            </Button>
          </Box>
        </Paper>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <PdfSideViewer fileUrl={previewUrl} initialPage={1} loading={previewLoading} />
        </Box>
      </DialogContent>
    </Dialog>
  );
}
